import './index.less'
import RegularColumn from '../../../../../components/dataTable/components/regularColumn'
import {
  AccessActionsLabelMapper,
  AccessLevelOptions,
  AccessLevelOptionsLabelMapper,
  AccessLevelOptionsTooltipMapper,
  IPermission,
  PermissionActionValues,
  PermissionGroup,
  PermissionModulesLabelMapper,
  PermissionType
} from '../../../constants'
import TableCardViewV2 from '../../../../../components/TableCardViewV2/tableCardViewV2'
import { updateRolePermission, UpdateRolePermissionsParams } from '../../../slice'
import { useIntl } from 'react-intl'
import React from 'react'
import { Checkbox, Icon, RadioGroup, Tooltip, Typography } from '@lightbeamai/design-system'
import { connect } from 'react-redux'

interface IProps {
  permissions?: IPermission[]
  isDetailsView?: boolean
  tableHeader?: string
  updateRolePermission: (params: UpdateRolePermissionsParams) => void
}

const CustomAccessTable = ({
  permissions,
  isDetailsView = true,
  tableHeader,
  updateRolePermission
}: IProps) => {
  const intl = useIntl()

  const RadioButtons = (props: {
    permissions: IPermission
    onChange: (accessLevel: AccessLevelOptions) => void
  }) => {
    const {
      currentAccessLevel,
      accessOptions = [],
      permissionGroup,
      permissionName
    } = props.permissions

    const handleChange = (e) => props.onChange(e.target.value)

    return (
      <RadioGroup
        orientation="horizontal"
        disabled={isDetailsView}
        value={currentAccessLevel}
        onChange={handleChange}
        className="action-radio-group"
        options={accessOptions?.map((option) => {
          const labelKey =
            AccessLevelOptionsLabelMapper[
              permissionGroup === PermissionGroup.PRIVACY_OPS
                ? `${permissionName}-${option}`
                : option
            ] || AccessLevelOptionsLabelMapper[option]

          const privacyOpsTooltipKey =
            option !== AccessLevelOptions.NO_ACCESS
              ? AccessLevelOptionsTooltipMapper[`${permissionName}-${option}`]
              : undefined

          const nonPrivacyTooltipKey =
            option === AccessLevelOptions.LIMITED_ACCESS
              ? AccessLevelOptionsTooltipMapper[`${permissionName}-${option}`] ||
                'settings.roles.permissions.limitedAccess.tooltip'
              : undefined

          return {
            value: option,
            children: (
              <Typography className="align-center">
                {labelKey ? intl.formatMessage({ id: labelKey }) : option}
                {permissionGroup === PermissionGroup.PRIVACY_OPS && privacyOpsTooltipKey && (
                  <Tooltip title={intl.formatMessage({ id: privacyOpsTooltipKey })} placement="top">
                    <Icon name="InfoCircle" width={12} height={12} className="xs-ml-8" />
                  </Tooltip>
                )}
                {permissionGroup !== PermissionGroup.PRIVACY_OPS && nonPrivacyTooltipKey && (
                  <Tooltip title={intl.formatMessage({ id: nonPrivacyTooltipKey })} placement="top">
                    <Icon name="InfoCircle" width={12} height={12} className="xs-ml-8" />
                  </Tooltip>
                )}
              </Typography>
            )
          }
        })}
      />
    )
  }

  const Checkboxes = (props: {
    permissions: IPermission
    onChange: (currentActions: PermissionActionValues[]) => void
  }) => {
    const { currentActions = [], availableActions = [] } = props.permissions

    return (
      <div className="checkboxes">
        {availableActions?.map((action) => {
          const isChecked = currentActions.includes(action.value)
          const handleChange = () => {
            const newActions = isChecked
              ? currentActions.filter((a) => a !== action.value)
              : [...currentActions, action.value]
            props.onChange(newActions)
          }
          return (
            <Checkbox
              key={action.value}
              checked={isChecked}
              onChange={handleChange}
              size={24}
              className="action-checkbox"
              disabled={isDetailsView}
            >
              {intl.formatMessage({ id: AccessActionsLabelMapper[action.value] || '' })}
            </Checkbox>
          )
        })}
      </div>
    )
  }

  const columns = [
    {
      title: tableHeader || intl.formatMessage({ id: 'settings.roles.table.column.moduleAction' }),
      dataKey: 'moduleAction',
      minWidth: 200,
      cellRenderer: function RegularRenderer({ data }) {
        const { permissionName } = data

        return (
          <RegularColumn
            content={
              <Typography>
                {permissionName &&
                  intl.formatMessage({ id: PermissionModulesLabelMapper[permissionName] })}
              </Typography>
            }
          />
        )
      }
    },
    {
      title: intl.formatMessage({ id: 'settings.roles.table.column.permissions' }),
      dataKey: 'permissions',
      minWidth: 600,
      cellRenderer: function RegularRenderer({ data }) {
        const {
          type = PermissionType.RADIO,
          accessOptions = [],
          currentAccessLevel,
          availableActions = [],
          currentActions = [],
          permissionName,
          allowPermissionUpdate
        } = data as IPermission

        const handleAccessLevelChange = (currentAccessLevel: AccessLevelOptions) => {
          updateRolePermission({ permissionName, currentAccessLevel, type })
        }
        const handleAccessActionsChange = (currentActions: PermissionActionValues[]) => {
          updateRolePermission({ permissionName, currentActions, type })
        }

        const showRadioOptions =
          type === PermissionType.RADIO && accessOptions?.length > 1 && allowPermissionUpdate
        const showCheckboxes =
          type === PermissionType.CHECKBOX && availableActions?.length > 1 && allowPermissionUpdate

        if (!allowPermissionUpdate) {
          return (
            <RegularColumn
              content={
                <Typography>
                  {type === PermissionType.RADIO &&
                    currentAccessLevel &&
                    intl.formatMessage({ id: AccessLevelOptionsLabelMapper[currentAccessLevel] })}
                  {type === PermissionType.CHECKBOX &&
                    currentActions?.map((action, idx) => (
                      <span key={action}>
                        {idx > 0 && ', '}
                        {intl.formatMessage({ id: AccessActionsLabelMapper[action] || '' })}
                      </span>
                    ))}
                </Typography>
              }
            />
          )
        }

        if (showRadioOptions) {
          return (
            <RegularColumn
              showTooltip={false}
              content={<RadioButtons permissions={data} onChange={handleAccessLevelChange} />}
            />
          )
        }

        if (showCheckboxes) {
          return (
            <RegularColumn
              showTooltip={false}
              content={<Checkboxes permissions={data} onChange={handleAccessActionsChange} />}
            />
          )
        }
        return null
      }
    }
  ]

  const tableData = {
    list: permissions ? permissions.filter((perm) => perm.showPermission) : [],
    columns
  }

  return (
    <div className="widget-table-role-permissions-container">
      <TableCardViewV2
        pageName={'role-details-permissions'}
        tableData={tableData}
        totalCount={permissions?.length || 0}
        showTableLegend={false}
        className="widget-table-role-permissions"
        isPaginated={false}
      />
    </div>
  )
}

const mapDispatchToProps = {
  updateRolePermission
}

export default connect(null, mapDispatchToProps)(CustomAccessTable)
